import { Encoding } from '@mode-vegas/vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';

/**
 * Get all column names from multiple sources (visible + hidden columns).
 * Used for duplicate column name validation during column rename operations.
 *
 * This function checks three sources in order:
 * 1. Visible columns from the columns array
 * 2. Hidden columns from the encoding (encoding.marks.properties.text.values)
 * 3. Additional attributes (e.g., from model/view)
 *
 * @param columns - Array of visible columns from query results
 * @param encoding - Optional encoding that contains all fields (visible + hidden)
 * @param attrs - Optional array of attributes with names (e.g., from model view attrs)
 * @returns Array of unique column names (case-preserved)
 *
 * @example
 * ```typescript
 * const allNames = getAllColumnNamesFromSources(
 *   visibleColumns,
 *   encoding,
 *   viewAttrs
 * );
 * // Returns: ['Column1', 'Column2', 'HiddenColumn']
 * ```
 */
export function getAllColumnNamesFromSources(
  columns?: FlamingoTypes.RecordsResult['columns'],
  encoding?: Encoding,
  attrs?: ReadonlyArray<{ name: string }>
): string[] {
  const allNames: Set<string> = new Set();

  // Add visible columns
  if (columns?.length) {
    columns.forEach((col) => allNames.add(col.name));
  }

  // Add hidden columns from encoding
  // The encoding contains ALL fields (visible + hidden)
  if (encoding?.marks?.properties?.text?.values) {
    encoding.marks.properties.text.values.forEach((field) => {
      // Extract column name from formula like "[columnName]"
      const match = field.formula.match(/^\[(.*?)\]$/);
      if (match?.[1]) {
        allNames.add(match[1]);
      }
    });
  }

  // Add from attrs if available
  if (attrs?.length) {
    attrs.forEach((attr) => allNames.add(attr.name));
  }

  return Array.from(allNames);
}
